import { useEffect, useState } from 'react';
import {
  getIntegrationStatus,
  getGoogleAuthUrl,
  disconnectGoogle,
  backupDatabaseNow,
  listDriveFiles,
  uploadToDrive,
  downloadDriveFile,
  type IntegrationStatus,
  type DriveFile,
} from '@/lib/api/integrations';
import apiClient from '@/lib/api/client';

const Card = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
    <div className="p-6 border-b border-gray-200">
      <h3 className="text-gray-900 text-lg font-bold leading-tight tracking-[-0.015em]">{title}</h3>
      {description && <p className="text-gray-600 text-sm mt-1">{description}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Pill = ({ ok, label }: { ok: boolean; label: string }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${ok ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'}`}>
    <span className="material-symbols-outlined text-sm">{ok ? 'check_circle' : 'radio_button_unchecked'}</span>
    {label}
  </span>
);

interface StripeStatus { configured: boolean; publishableKey: string | null; }
interface SmsStatus { configured: boolean; provider: string | null; fromNumber: string | null; }

export const IntegrationsTab = () => {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
  const [smsStatus, setSmsStatus] = useState<SmsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [files, setFiles] = useState<DriveFile[]>([]);

  const refresh = async () => {
    try {
      const [googleStatus, stripeRes, smsRes] = await Promise.allSettled([
        getIntegrationStatus(),
        apiClient.get('/payments/status'),
        apiClient.get('/integrations/sms/status'),
      ]);
      setStatus(googleStatus.status === 'fulfilled' ? googleStatus.value : null);
      setStripeStatus(stripeRes.status === 'fulfilled' ? stripeRes.value.data : null);
      setSmsStatus(smsRes.status === 'fulfilled' ? smsRes.value.data : null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') setMsg('Google account connected.');
    if (params.get('google') === 'error') setMsg('Google connection failed. Please try again.');
  }, []);

  const connect = async () => {
    setBusy('connect');
    try {
      window.location.href = await getGoogleAuthUrl();
    } catch {
      setMsg('Could not start Google authorization (check OAuth credentials).');
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await disconnectGoogle();
      await refresh();
      setMsg('Disconnected Google account.');
    } finally {
      setBusy(null);
    }
  };

  const runBackup = async () => {
    setBusy('backup');
    setMsg(null);
    try {
      const res = await backupDatabaseNow();
      setMsg(res.success ? `Backup uploaded: ${res.data?.name}` : `Backup failed: ${res.message}`);
    } catch {
      setMsg('Backup request failed.');
    } finally {
      setBusy(null);
    }
  };

  const loadFiles = async () => {
    setBusy('files');
    try {
      setFiles(await listDriveFiles());
    } catch {
      setMsg('Could not list Drive files (connect Google first).');
    } finally {
      setBusy(null);
    }
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('upload');
    try {
      await uploadToDrive(file);
      setMsg(`Uploaded ${file.name} to Drive.`);
      await loadFiles();
    } catch {
      setMsg('Upload failed (connect Google first).');
    } finally {
      setBusy(null);
      e.target.value = '';
    }
  };

  if (loading) {
    return <div className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />;
  }

  const s = status;
  const authorized = !!s?.authorized;

  return (
    <div className="space-y-6">
      {msg && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm font-medium text-primary">{msg}</div>
      )}

      <Card title="Google Account" description="Connect a Google account to enable Calendar sync and Drive file storage. Credentials are read from server .env.">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Pill ok={!!s?.oauthConfigured} label="OAuth configured" />
          <Pill ok={authorized} label="Account connected" />
          <Pill ok={!!s?.mapsConfigured} label="Maps key" />
          <Pill ok={!!s?.driveBackupConfigured} label="Drive backup" />
        </div>
        {!s?.oauthConfigured && (
          <p className="text-sm text-gray-500 mb-4">
            Add <code>GOOGLE_OAUTH_CLIENT_ID</code> and <code>GOOGLE_OAUTH_CLIENT_SECRET</code> to the server <code>.env</code>, then restart.
          </p>
        )}
        <div className="flex gap-3">
          {!authorized ? (
            <button
              onClick={connect}
              disabled={!s?.oauthConfigured || busy === 'connect'}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">link</span>
              Connect Google
            </button>
          ) : (
            <button
              onClick={disconnect}
              disabled={busy === 'disconnect'}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">link_off</span>
              Disconnect
            </button>
          )}
        </div>
      </Card>

      <Card title="Database Backup" description="Back up the database to Google Drive. Runs automatically every 24h when connected and a backup folder is set.">
        <button
          onClick={runBackup}
          disabled={!authorized || busy === 'backup'}
          className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-lg">cloud_upload</span>
          {busy === 'backup' ? 'Backing up…' : 'Back Up Now'}
        </button>
        {!s?.driveBackupConfigured && (
          <p className="text-sm text-gray-500 mt-3">
            Set <code>GOOGLE_DRIVE_BACKUP_FOLDER_ID</code> in <code>.env</code> to choose the destination folder.
          </p>
        )}
      </Card>

      <Card title="Drive Files" description="Send files to and retrieve files from the connected Drive folder.">
        <div className="flex flex-wrap gap-3 mb-4">
          <label className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 cursor-pointer ${authorized ? 'text-white bg-primary hover:bg-primary/90' : 'text-gray-400 bg-gray-100 cursor-not-allowed'}`}>
            <span className="material-symbols-outlined text-lg">upload_file</span>
            Upload File
            <input type="file" className="hidden" onChange={onUpload} disabled={!authorized || busy === 'upload'} />
          </label>
          <button
            onClick={loadFiles}
            disabled={!authorized || busy === 'files'}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-lg">refresh</span>
            Refresh List
          </button>
        </div>
        {files.length > 0 ? (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {files.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                <span className="truncate text-gray-800">{f.name}</span>
                <button
                  onClick={() => downloadDriveFile(f).catch(() => setMsg('Download failed.'))}
                  className="shrink-0 text-primary hover:underline flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-base">download</span>
                  Download
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No files loaded. Connect Google and refresh.</p>
        )}
      </Card>

      {/* Stripe Payments */}
      <Card title="Stripe Payments" description="Accept credit card payments on invoices. Add keys to server .env to activate.">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Pill ok={!!stripeStatus?.configured} label="Stripe configured" />
        </div>
        {stripeStatus?.configured ? (
          <div className="space-y-3">
            <p className="text-sm text-success font-medium flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              Stripe is active. "Pay Now" buttons appear on sent invoices.
            </p>
            {stripeStatus.publishableKey && (
              <p className="text-xs text-gray-500 font-mono bg-gray-50 rounded px-3 py-2 border border-gray-200">
                Publishable key: {stripeStatus.publishableKey.slice(0, 20)}…
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-500">Add the following keys to <code className="text-xs bg-gray-100 px-1 rounded">.env</code> on the server and restart:</p>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-1">
              <div className="text-gray-600">STRIPE_SECRET_KEY=sk_live_…</div>
              <div className="text-gray-600">STRIPE_PUBLISHABLE_KEY=pk_live_…</div>
              <div className="text-gray-600">STRIPE_WEBHOOK_SECRET=whsec_…</div>
            </div>
            <p className="text-xs text-gray-400">Use <code>sk_test_</code> / <code>pk_test_</code> keys for testing.</p>
          </div>
        )}
      </Card>

      {/* SMS / Twilio */}
      <Card title="SMS Notifications" description="Send appointment reminders and timesheet nudges via SMS. Powered by Twilio.">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Pill ok={!!smsStatus?.configured} label="SMS configured" />
          {smsStatus?.provider && <Pill ok label={smsStatus.provider} />}
        </div>
        {smsStatus?.configured ? (
          <div className="space-y-2">
            <p className="text-sm text-success font-medium flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              SMS is active via {smsStatus.provider}.
            </p>
            {smsStatus.fromNumber && (
              <p className="text-sm text-gray-600">Sending from: <span className="font-mono">{smsStatus.fromNumber}</span></p>
            )}
            <p className="text-xs text-gray-500 mt-2">Hour-end reminders fire at 16:30 and 18:00 (America/Vancouver) for employees without a submitted timesheet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-500">Add Twilio keys to <code className="text-xs bg-gray-100 px-1 rounded">.env</code> on the server and restart:</p>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-1">
              <div className="text-gray-600">SMS_PROVIDER=twilio</div>
              <div className="text-gray-600">SMS_FROM=+12505550100</div>
              <div className="text-gray-600">TWILIO_ACCOUNT_SID=AC…</div>
              <div className="text-gray-600">TWILIO_AUTH_TOKEN=…</div>
            </div>
            <p className="text-xs text-gray-400">Without these keys, the reminder scheduler is inert and falls back to email.</p>
          </div>
        )}
      </Card>
    </div>
  );
};
